// The workshop's data (lives only here, not in delve-portal): the game's Hero Sheet (delve-game Docs/heros:
// content.json + media/), uploaded to R2 by delve-backend's `make prod-publish-workshop` (dev bucket: `make
// publish-workshop`; both run `delve-portal workshop publish`, admin/domain/workshop_content.py). The dev server
// (vite.config.ts) serves the sheet straight from the game's folder and strips it the same way: keep the two in step.
// Several packs (packs.json, /workshop-media/packs/<id>/...), the weapon export (weapons.json and its renders), the
// biome catalog and design files (biomes.json, designs.json) and the clips' web copies (sounds/, named by each slot's
// clipFiles).

/** The pack the portal shows (ADR 0005); its files sit under workshop/<pack>/ in the releases bucket. */
export const WORKSHOP_PACK = "heroicfantasy";

interface SoundSlot {
  clips?: unknown[];
  clipCount?: number;
  clipFiles?: string[];
  [key: string]: unknown;
}

/** A clip the browser can load: a path relative to the pack's files (media/...), never one on the rendering machine. */
export function isWebClip(clip: unknown): clip is string {
  return typeof clip === "string" && clip !== "" && !clip.startsWith("/") && !clip.includes("\\") && !clip.includes(":")
    && clip.split("/").every((p) => p !== ".." && p !== "." && p !== "");
}

/** Where a clip's web copy goes: its path under the pack's Audio folder (sounds/CUE_X/X_00.m4a), as the backend's
 *  `clip_key`. The dev server keeps the file's own extension (it serves the .wav). */
export function clipKey(clip: string, extension = ".m4a"): string {
  const parts = clip.replaceAll("\\", "/").split("/").filter(Boolean);
  const audio = parts.lastIndexOf("Audio");
  const under = audio >= 0 ? parts.slice(audio + 1) : parts.slice(-1);
  return "sounds/" + under.join("/").replace(/\.[^./]+$/, "") + extension;
}

function dropClips(slot: SoundSlot, clipFiles?: (clip: string) => string | null): void {
  const clips = Array.isArray(slot.clips) ? slot.clips : [];
  slot.clipCount = clips.length;
  const web = clips.filter(isWebClip);
  if (web.length) slot.clips = web;
  else delete slot.clips;
  if (clipFiles) {
    slot.clipFiles = clips.flatMap((c) => {
      const key = typeof c === "string" ? clipFiles(c) : null;
      return key ? [key] : [];
    });
  }
}

/** content.json without the clips' paths on the machine that rendered the sheet, its media in "media". With
 *  `clipFiles` (the dev server), each slot names the web path of its clips that are on this machine. */
export function publishedContent(
  raw: Record<string, unknown>, clipFiles?: (clip: string) => string | null,
): Record<string, unknown> {
  const content = structuredClone(raw);
  content.media = "media";
  for (const character of (content.characters as { sounds?: SoundSlot[] }[] | undefined) ?? []) {
    for (const slot of character.sounds ?? []) dropClips(slot, clipFiles);
  }
  for (const slot of (content.gameSounds as SoundSlot[] | undefined) ?? []) dropClips(slot, clipFiles);
  return content;
}

const IMAGE = /\.(png|webp|jpg|gif)$/;
const SOUND = /\.(m4a|wav|ogg)$/;
const JSON_FILES = new Set(["content.json", "tunables.json", "weapons.json", "biomes.json", "designs.json"]);

/** A path of one pack that may be served: its JSON files, a file in media/, a clip in sounds/, or a weapon render (the
 *  export's "image", in a weapons* folder next to it). */
export function workshopPath(parts: string[]): string | null {
  if (parts.length === 0 || parts.some((p) => p === ".." || p === "." || p === "")) return null;
  const path = parts.join("/");
  if (JSON_FILES.has(path)) return path;
  if (parts[0] === "media" && parts.length >= 3) return path;
  if (parts[0] === "sounds" && parts.length >= 2 && SOUND.test(path)) return path;
  return parts[0]!.startsWith("weapons") && parts.length >= 2 && IMAGE.test(path) ? path : null;
}

const PACK_ID = /^[a-z0-9][a-z0-9_-]{0,63}$/;

/** /workshop-media/<parts>: packs.json (the list of packs), a file of the portal's pack, or packs/<id>/<file> of
 *  another. `key` is the file's key under workshop/ in the bucket. */
export function workshopFile(parts: string[]): { pack: string | null; path: string; key: string } | null {
  if (parts.length === 1 && parts[0] === "packs.json") return { pack: null, path: "packs.json", key: "packs.json" };
  let pack = WORKSHOP_PACK;
  if (parts[0] === "packs") {
    if (!PACK_ID.test(parts[1] ?? "")) return null;
    pack = parts[1]!;
    parts = parts.slice(2);
  }
  const path = workshopPath(parts);
  return path ? { pack, path, key: `${pack}/${path}` } : null;
}
